import {
  takeEvery,
  takeLatest,
  all,
  call,
  put,
  select,
} from 'redux-saga/effects';
import {
  fetchBridgedResource,
  defineKqlQuery,
  searchSubmissions,
  fetchTeams,
  createTeam,
  deleteTeam,
  createMembership,
  deleteMembership,
  createSubmission,
  deleteSubmission,
} from '@kineticdata/react';
import { types, actions } from '../modules/settingsApprovals';
import { Utils, C, selectServicesKapp } from '@kineticdata/bundle-common';
import { List, Map } from 'immutable';
import md5 from 'md5';

export function* fetchTeamsSaga() {
  const { teams, error } = yield call(fetchTeams, {
    limit: 1000,
    // include: 'attributes,memberships.user',
  });

  if (error) {
    yield put(actions.fetchTeamsFailure(error));
  } else {
    const agreementsMap = yield select(
      state => state.settingsApp.agreementsMap,
    );
    // Filter teams to only those that are agreement based
    const agreementTeams = List(teams).filter(team =>
      agreementsMap.has(team.name.split('::')[0]),
    );
    // Group by level of team in the hierarchy
    const agreementTeamsMap = agreementTeams
      .groupBy(team => team.name.split('::')[0])
      .map((teams, rootName) =>
        Map({
          team: teams.find(t => t.name === rootName),
          master: teams.find(t => t.name === `${rootName}::Master`),
          children: teams.filter(
            t =>
              t.name.split('::').length === 2 &&
              t.name !== `${rootName}::Master`,
          ),
        }),
      )
      .toMap();

    yield put(actions.fetchTeamsSuccess(agreementTeamsMap));
  }
}

export function* fetchAgreementTeamSaga({ payload }) {
  // Check if this agreement team has been fetched already
  const isInit = yield select(state => !!state.settingsApprovals.agreementTeam);
  const isCustomerApprovalAdmin = yield select(
    state => state.settingsApp.isCustomerApprovalAdmin,
  );

  const [{ teams, error }] = yield all([
    call(fetchTeams, {
      q: `name =* "${payload}"`,
      limit: 1000,
      include: 'attributes,memberships.user',
    }),
    // Only fetch the below supplemental data if this is the first time we're
    // fetching this agreement team.
    !isInit && call(fetchAgreementUsersSaga, { payload }),
    !isInit && call(fetchApprovalFormsSaga, { payload }),
  ]);

  if (error) {
    yield put(actions.fetchAgreementTeamFailure(error));
  } else {
    const isValidAgreement = yield select(state =>
      state.settingsApp.agreementsMap.has(payload),
    );
    const team = teams.find(team => team.name === payload);
    const master = teams.find(team => team.name === `${payload}::Master`);

    // If the agreement team or its master team doesn't exist, and the user is a
    // customer approval admin, and the agreement is valid, create the team(s).
    if ((!team || !master) && isCustomerApprovalAdmin && isValidAgreement) {
      const [teamResults] = yield all([
        !team &&
          call(createTeam, {
            team: {
              name: payload,
              description: `Agreement Team ${payload}`,
            },
          }),
        !master &&
          call(createTeam, {
            team: {
              name: `${payload}::Master`,
              description: `Master Approval Managers for Agreement Team ${payload}`,
            },
          }),
      ]);

      if (teamResults && teamResults.error) {
        yield put(actions.fetchAgreementTeamFailure(teamResults.error));
      } else {
        const newResults = yield call(fetchTeams, {
          q: `name =* "${payload}"`,
          limit: 1000,
          include: 'attributes,memberships.user',
        });
        if (error) {
          yield put(actions.fetchAgreementTeamFailure(newResults.error));
        } else {
          yield put(
            actions.fetchAgreementTeamSuccess({
              team: newResults.teams.find(team => team.name === payload),
              master: newResults.teams.find(
                team => team.name === `${payload}::Master`,
              ),
              children: newResults.teams.filter(
                team =>
                  team.name.split('::').length === 2 &&
                  team.name !== `${payload}::Master`,
              ),
            }),
          );
        }
      }
    } else {
      yield put(
        actions.fetchAgreementTeamSuccess({
          team,
          master,
          children: teams.filter(
            team =>
              team.name.split('::').length === 2 &&
              team.name !== `${payload}::Master`,
          ),
        }),
      );
    }
  }
}

export function* fetchAgreementUsersSaga({ payload }) {
  // Only fetch agreement users the data isn't already fetched since nothing in
  // the settings console will change the results of this call so there is no
  // need to refetch this data for the same agreement.
  const shouldFetch = yield select(
    state => !state.settingsApprovals.agreementUsers,
  );
  if (shouldFetch) {
    const servicesKapp = yield select(selectServicesKapp);

    const { records, error } = yield call(fetchBridgedResource, {
      kappSlug: servicesKapp && servicesKapp.slug,
      formSlug: C.FORM_SLUG_SHARED_RESOURCES,
      bridgedResourceName: 'Agreement Users',
      values: { Agreement: payload },
    });

    if (error) {
      yield put(actions.fetchAgreementUsersFailure(error));
    } else {
      yield put(actions.fetchAgreementUsersSuccess(records));
    }
  }
}

const mapApprovalForms = (submissions, formsMap) =>
  submissions
    .map(s => {
      const form = formsMap.get(s.values['Form Slug']);
      const formData = form
        ? {
            'Form Name': form.name,
            'Funding Dependent': Utils.hasAttributeValue(
              form,
              C.FORM_ATTR_FUNDING_DEPENDENT,
              'True',
            ),
          }
        : {};
      return { id: s.id, ...s.values, ...formData };
    })
    .sort((a, b) => {
      return a['Form Name'] > b['Form Name']
        ? 1
        : a['Form Name'] < b['Form Name']
          ? -1
          : 0;
    });

export function* fetchApprovalFormsSaga({ payload }) {
  const { submissions, error } = yield call(searchSubmissions, {
    kapp: C.KAPP_SLUG_ADMIN,
    form: C.FORM_SLUG_FORM_APPROVALS,
    limit: 1000,
    search: {
      q: defineKqlQuery()
        .equals(`values[${C.FIELD_AGREEMENT}]`, 'agreement')
        .end()({
        agreement: payload,
      }),
      include: ['details', 'values'],
    },
  });

  if (error) {
    yield put(actions.fetchApprovalFormsFailure(error));
  } else {
    const servicesKapp = yield select(selectServicesKapp);
    const formsMap = List((servicesKapp && servicesKapp.forms) || [])
      .groupBy(f => f.slug)
      .map(v => v.first());

    yield put(
      actions.fetchApprovalFormsSuccess(
        mapApprovalForms(submissions, formsMap),
      ),
    );
  }
}

export function* createApprovalTeamSaga({ payload = {} }) {
  const { success, failure, complete, agreement, name } = payload;

  if (!agreement || !name) {
    if (typeof failure === 'function') {
      failure({
        message: 'Invalid arguments provided for creating an approval group.',
      });
    }
  } else if (name.indexOf('::') >= 0) {
    if (typeof failure === 'function') {
      failure({
        message: "Approval group names cannot contain '::'.",
      });
    }
  } else {
    const { team, error } = yield call(createTeam, {
      team: {
        name: `${agreement}::${name}`,
        description: `Approval Group for Agreement Team ${agreement}`,
      },
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (team) {
      if (typeof success === 'function') {
        success(team);
      }

      yield put(actions.fetchAgreementTeamRequest(agreement));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* deleteApprovalTeamSaga({ payload }) {
  const { success, failure, complete, teamName } = payload;
  const parts = teamName ? teamName.split('::') : [];
  const isValidAgreement = yield select(state =>
    state.settingsApp.agreementsMap.has(parts[0]),
  );

  if (!teamName || parts.length !== 2 || !isValidAgreement) {
    if (typeof failure === 'function') {
      failure({
        message: 'Invalid arguments provided for deleting an approval group.',
      });
    }
  } else {
    const { team, error } = yield call(deleteTeam, {
      teamSlug: md5(teamName),
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (team) {
      if (typeof success === 'function') {
        success(team);
      }

      yield put(actions.fetchAgreementTeamRequest(parts[0]));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* addApprovalManagerSaga({ payload = {} }) {
  const { success, failure, complete, teamName, username, master } = payload;
  const parts = teamName ? teamName.split('::') : [];

  if (!teamName || !username || parts.length !== 1) {
    if (typeof failure === 'function') {
      failure({
        message: 'Invalid arguments provided for adding an approval manager.',
      });
    }
  } else {
    const { membership, error } = yield call(createMembership, {
      team: { name: teamName },
      user: { username: username },
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (membership) {
      // If adding a master approval manager, add them to the Master subteam
      if (!!master) {
        yield call(createMembership, {
          team: { name: `${teamName}::Master` },
          user: { username: username },
        });
      }

      if (typeof success === 'function') {
        success(membership);
      }

      yield put(actions.fetchAgreementTeamRequest(parts[0]));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* removeApprovalManagerSaga({ payload = {} }) {
  const { success, failure, complete, teamName, username } = payload;
  const parts = teamName ? teamName.split('::') : [];

  if (!teamName || !username || parts.length !== 1) {
    if (typeof failure === 'function') {
      failure({
        message: 'Invalid arguments provided for removing an approval manager.',
      });
    }
  } else {
    const { membership: membershipObject, error, data } = yield call(
      deleteMembership,
      {
        teamSlug: md5(teamName),
        username: username,
        include: 'user.memberships',
      },
    );
    // TODO remove this line and update the call above to use only the
    // membership object one the api call is fixed in the library
    const membership = membershipObject || (data ? data.membership : null);

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (membership) {
      // If removing a master approval manager, remove them from the Master subteam
      if (
        !!membership.user.memberships.find(
          m => m.team.name === `${teamName}::Master`,
        )
      ) {
        yield call(deleteMembership, {
          teamSlug: md5(`${teamName}::Master`),
          username: username,
        });
      }

      if (typeof success === 'function') {
        success(membership);
      }

      yield put(actions.fetchAgreementTeamRequest(parts[0]));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* addApproverSaga({ payload = {} }) {
  const { success, failure, complete, teamName, username } = payload;
  const parts = teamName ? teamName.split('::') : [];

  if (!teamName || !username || parts.length !== 2) {
    if (typeof failure === 'function') {
      failure({
        message: 'Invalid arguments provided for adding an approver.',
      });
    }
  } else {
    const { membership, error } = yield call(createMembership, {
      team: { name: teamName },
      user: { username: username },
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (membership) {
      if (typeof success === 'function') {
        success(membership);
      }

      yield put(actions.fetchAgreementTeamRequest(parts[0]));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* removeApproverSaga({ payload = {} }) {
  const { success, failure, complete, teamName, username } = payload;
  const parts = teamName ? teamName.split('::') : [];

  if (!teamName || !username || parts.length !== 2) {
    if (typeof failure === 'function') {
      failure({
        message: 'Invalid arguments provided for removing an approver.',
      });
    }
  } else {
    const { membership: membershipObject, error, data } = yield call(
      deleteMembership,
      {
        teamSlug: md5(teamName),
        username: username,
      },
    );
    // TODO remove this line and update the call above to use only the
    // membership object one the api call is fixed in the library
    const membership = membershipObject || (data ? data.membership : null);

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (membership) {
      if (typeof success === 'function') {
        success(membership);
      }

      yield put(actions.fetchAgreementTeamRequest(parts[0]));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* addApprovalFormSaga({ payload = {} }) {
  const { success, failure, complete, teamName, formSlug, type } = payload;
  const parts = teamName ? teamName.split('::') : [];

  if (!teamName || !formSlug || !type || parts.length !== 2) {
    if (typeof failure === 'function') {
      failure({
        message:
          'Invalid arguments provided for adding a service offering to an approval group.',
      });
    }
  } else {
    const { submission, error } = yield call(createSubmission, {
      kappSlug: C.KAPP_SLUG_ADMIN,
      formSlug: C.FORM_SLUG_FORM_APPROVALS,
      values: {
        Agreement: parts[0],
        'Approval Group': teamName,
        'Form Slug': formSlug,
        Type: type,
      },
      include: 'values',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      yield put(actions.fetchApprovalFormsRequest(parts[0]));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* removeApprovalFormSaga({ payload = {} }) {
  const { success, failure, complete, id } = payload;

  if (!id) {
    if (typeof failure === 'function') {
      failure({
        message:
          'Invalid arguments provided for removing a service offering from an approval group.',
      });
    }
  } else {
    const { submission, error } = yield call(deleteSubmission, {
      id,
      include: 'values',
    });

    if (error && typeof failure === 'function') {
      failure(error);
    } else if (submission) {
      if (typeof success === 'function') {
        success(submission);
      }

      const agreement = yield select(
        state => state.settingsApprovals.currentAgreementTeam,
      );
      yield put(actions.fetchApprovalFormsRequest(agreement));
    }
  }

  if (typeof complete === 'function') {
    complete();
  }
  yield put(actions.actionComplete());
}

export function* watchSettingsApprovals() {
  yield takeEvery(types.FETCH_TEAMS_REQUEST, fetchTeamsSaga);
  yield takeLatest(types.FETCH_AGREEMENT_TEAM_REQUEST, fetchAgreementTeamSaga);
  yield takeLatest(types.FETCH_APPROVAL_FORMS_REQUEST, fetchApprovalFormsSaga);
  yield takeEvery(types.CREATE_APPROVAL_TEAM_REQUEST, createApprovalTeamSaga);
  yield takeEvery(types.DELETE_APPROVAL_TEAM_REQUEST, deleteApprovalTeamSaga);
  yield takeEvery(types.ADD_APPROVAL_MANAGER_REQUEST, addApprovalManagerSaga);
  yield takeEvery(
    types.REMOVE_APPROVAL_MANAGER_REQUEST,
    removeApprovalManagerSaga,
  );
  yield takeEvery(types.ADD_APPROVER_REQUEST, addApproverSaga);
  yield takeEvery(types.REMOVE_APPROVER_REQUEST, removeApproverSaga);
  yield takeEvery(types.ADD_APPROVAL_FORM_REQUEST, addApprovalFormSaga);
  yield takeEvery(types.REMOVE_APPROVAL_FORM_REQUEST, removeApprovalFormSaga);
}
